// Step timing for the capture flow: what happened, when, and how slow.
//
// Records are buffered and posted in batches to /api/capture/log, which writes
// one `[capture-step]` line each. Fire and forget: nothing here throws, awaits
// or blocks a tap. No tokens, cookies or names; barcodes are fine.

/** A step slower than this (ms) is flagged. Human-paced steps are generous. */
export const SLOW_MS = {
  session_checked: 1500,
  link_redeem: 2500,
  camera_start: 3000,
  scanner_ready: 2000,
  barcode_lock: 8000,
  lookup: 1500,
  sheet_choice: 15000,
  photo_step_shown: 1000,
  shot: 1500,
  stage_upload: 4000,
  filed: 5000,
  commit: 3000,
  finish: 15000,
  call: 5000,
};
const DEFAULT_SLOW_MS = 3000;

const FLUSH_EVERY_MS = 5000;
const BATCH = 50;
const KEEP = 200;
// The server refuses a body over ~16 KB; stay under it.
const MAX_BODY = 15000;
const MAX_KEYS = 10;

/** Extras as flat primitives only, short strings, at most MAX_KEYS keys. */
export function cleanExtra(extra) {
  const out = {};
  let n = 0;
  for (const [k, v] of Object.entries(extra || {})) {
    if (n >= MAX_KEYS) break;
    if (typeof v === "number") {
      if (!Number.isFinite(v)) continue;
      out[k] = Math.round(v);
    } else if (typeof v === "boolean") out[k] = v ? 1 : 0;
    else if (typeof v === "string") out[k] = v.slice(0, 40);
    else continue;
    n += 1;
  }
  return out;
}

/** The first `max` records whose JSON stays under the body cap. */
export function takeBatch(buf, max = BATCH, cap = MAX_BODY) {
  const out = [];
  let size = 2;
  for (const r of buf) {
    const s = JSON.stringify(r).length + 1;
    if (out.length >= max || (out.length && size + s > cap)) break;
    out.push(r);
    size += s;
  }
  return out;
}

/**
 * @param {{now: () => number, send: (records: object[]) => boolean, run: string, dev?: boolean}} o
 *   `now` is ms since page start; `send` returns false when it could not queue.
 */
export function createStepLog({ now, send, run, dev = false }) {
  let buf = [];

  function push(step, ms, extra) {
    const x = cleanExtra(extra);
    const dur = Math.max(0, Math.round(ms));
    const t = Math.round(now());
    const slow = dur > (SLOW_MS[step] ?? DEFAULT_SLOW_MS) ? 1 : 0;
    const rec = { run, step: String(step).slice(0, 32), t, ms: dur, slow, x };
    buf.push(rec);
    if (buf.length > KEEP) buf = buf.slice(buf.length - KEEP);
    if (dev) {
      const kv = Object.entries(x).map(([k, v]) => `${k}=${v}`).join(" ");
      console.info(`[step] ${rec.step} ms=${dur} at=${t}${slow ? " SLOW" : ""}${kv ? " " + kv : ""}`);
    }
    return rec;
  }

  return {
    /** An instant. `extra.ms`, when given, is its duration (measured by the caller). */
    mark(step, extra = {}) {
      const { ms = 0, ...rest } = extra;
      return push(step, typeof ms === "number" ? ms : 0, rest);
    },
    /** Start timing a step; the returned `end(extra)` records it. */
    span(step) {
      const t0 = now();
      let done = false;
      return (extra = {}) => {
        if (done) return null;
        done = true;
        return push(step, now() - t0, extra);
      };
    },
    /** Send what is buffered, a batch at a time. A refused batch stays queued. */
    flush() {
      while (buf.length) {
        const batch = takeBatch(buf);
        let ok = false;
        try {
          ok = send(batch) !== false;
        } catch {
          ok = false;
        }
        if (!ok) return;
        buf = buf.slice(batch.length);
      }
    },
    get size() {
      return buf.length;
    },
  };
}

// --- the page's own log ------------------------------------------------------

function runId() {
  try {
    return Array.from(crypto.getRandomValues(new Uint8Array(4)), (b) => b.toString(16).padStart(2, "0")).join("");
  } catch {
    return Math.random().toString(16).slice(2, 10);
  }
}

function beacon(records) {
  const body = JSON.stringify(records);
  if (typeof navigator !== "undefined" && navigator.sendBeacon) {
    return navigator.sendBeacon("/api/capture/log", new Blob([body], { type: "application/json" }));
  }
  // No beacon: a keepalive POST, never awaited.
  fetch("/api/capture/log", { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true }).catch(() => {});
  return true;
}

const page = createStepLog({
  now: () => (typeof performance !== "undefined" ? performance.now() : 0),
  send: beacon,
  run: runId(),
  dev: Boolean(import.meta.env?.DEV),
});

if (typeof window !== "undefined" && typeof document !== "undefined") {
  setInterval(() => page.flush(), FLUSH_EVERY_MS);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") page.flush();
  });
  window.addEventListener("pagehide", () => page.flush());
}

export const mark = (step, extra) => page.mark(step, extra);
export const span = (step) => page.span(step);
export const flush = () => page.flush();
